import axios from 'axios';

/**
 * P-SF5 — sending MANY leads to Salesforce, one at a time.
 *
 * There is no bulk write. This runs the SAME two single-lead endpoints the panel runs
 * (P-SF3 create, P-SF4 campaign membership), sequentially, and each of them re-checks
 * ownership, approval, state and org for itself. What lives here is only the decision of
 * WHICH of them to call for each lead, taken from the server's plan, and the bookkeeping of
 * what came back.
 *
 * Three rules shape all of it:
 *
 *   NOTHING IS ALL-OR-NOTHING. One lead's failure never stops the next — with the single
 *   exception of `salesforce_connection_changed`, because the user chose ONE org when the
 *   run started and half a batch must never land in another one.
 *
 *   A LOST ANSWER IS NOT A FAILURE. When a request never comes back, the write may well have
 *   happened: the lead is marked `unknown_response` and the final state is read back from the
 *   server afterwards, never guessed here.
 *
 *   THE PLAN IS A CONVENIENCE, NOT AN AUTHORITY. Every skip below only avoids a call the
 *   backend would refuse anyway; nothing here can make a write happen that the backend would
 *   not allow on its own.
 */

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

const authConfig = (token) => ({ headers: { Authorization: `Bearer ${token}` } });

// What the run reports per lead. `unknown_response` is internal: it means "the server knows,
// this browser does not", and the final read-back replaces it.
export const LEAD_CREATED = 'created';
export const LEAD_ALREADY_CREATED = 'already_created';
export const LEAD_DUPLICATE = 'duplicate';
export const LEAD_UNKNOWN = 'creation_unknown';
export const LEAD_BUSY = 'busy';
export const LEAD_FAILED = 'failed';
export const LEAD_NOT_ELIGIBLE = 'not_eligible';
export const LEAD_UNSAVED = 'unsaved_changes';
export const LEAD_NOT_PROCESSED = 'not_processed';
export const LEAD_UNKNOWN_RESPONSE = 'unknown_response';

export const CAMPAIGN_ADDED = 'added';
export const CAMPAIGN_ALREADY = 'already_member';
export const CAMPAIGN_FAILED = 'failed';
export const CAMPAIGN_SKIPPED = 'skipped';
export const CAMPAIGN_NONE = 'none';

export const CONNECTION_CHANGED = 'salesforce_connection_changed';

// The states a lead must be in for the run to try creating it. Everything else is either
// already done, deliberately never retried, or somebody else's attempt in flight.
const CREATABLE = ['ready', 'failed'];
// A create's stored answer, as this run reports it.
const CREATE_OUTCOME = {
  created: LEAD_CREATED,
  duplicate: LEAD_DUPLICATE,
  creation_unknown: LEAD_UNKNOWN,
  failed: LEAD_FAILED,
  creating: LEAD_BUSY,
};
// A membership's stored answer.
const MEMBER_OUTCOME = {
  added: CAMPAIGN_ADDED,
  already_member: CAMPAIGN_ALREADY,
  failed: CAMPAIGN_FAILED,
  adding: CAMPAIGN_SKIPPED,
};
// The plan's per-lead classification, for the dialog. `ready` here means "this run would
// try to create it", which is exactly the P-SF3 states a create is allowed from.
const PLAN_BUCKET = {
  ready: 'ready',
  failed: 'ready',
  created: 'already_created',
  duplicate: 'duplicate',
  creation_unknown: 'manual',
  creating: 'manual',
  not_mappable: 'not_eligible',
  not_eligible: 'not_eligible',
};

const entriesById = (plan) => new Map(
  ((plan && plan.leads) || []).map((entry) => [entry.lead_id, entry]));

/**
 * How the dialog describes the selection before anything is sent: one bucket per lead, in
 * the order they were selected, plus the counts.
 */
export const planSummary = (plan, leadIds, dirtyLeadIds) => {
  const entries = entriesById(plan);
  const buckets = {};
  const counts = {
    ready: 0, already_created: 0, duplicate: 0, manual: 0, not_eligible: 0, unsaved_changes: 0,
  };
  leadIds.forEach((leadId) => {
    const entry = entries.get(leadId);
    let bucket;
    if (dirtyLeadIds && dirtyLeadIds.has(leadId)) {
      // Unsaved edits are checked FIRST: Salesforce is sent what is persisted, so a lead
      // whose panel holds unsaved changes would be sent with different values than the ones
      // on screen. It waits for a Save instead.
      bucket = 'unsaved_changes';
    } else if (!entry || !entry.approved) {
      bucket = 'not_eligible';
    } else {
      bucket = PLAN_BUCKET[entry.creation_state] || 'manual';
    }
    buckets[leadId] = bucket;
    counts[bucket] += 1;
  });
  return { buckets, counts, total: leadIds.length };
};

/** Would this lead reach a Campaign write, given the plan and the Campaign chosen? */
const campaignIsSettled = (entry) => ['added', 'already_member'].includes(
  entry && entry.membership_state);

/**
 * Run the selection. Sequential by construction: one await per lead, and the next lead only
 * starts once the previous one is completely finished.
 *
 * `isCancelled` and `isMounted` are asked BETWEEN leads only — a request already sent is
 * always waited for, because aborting it here would not stop the write at the other end and
 * would only throw away the answer.
 */
export const runSalesforceBulk = async ({
  leadIds, plan, campaignId, token, dirtyLeadIds, isCancelled, isMounted, onProgress,
}) => {
  const entries = entriesById(plan);
  const expectedOrgId = (plan && plan.org_id) || null;
  const results = {};
  let stopCode = null;

  const report = (leadId, lead, patch) => {
    results[leadId] = { lead, campaign: CAMPAIGN_NONE, ...patch };
  };
  const progress = (started) => {
    if (onProgress) onProgress({ started, total: leadIds.length, results: { ...results } });
  };

  for (let index = 0; index < leadIds.length; index += 1) {
    const leadId = leadIds[index];

    // Between leads is the only place a run stops: cancelled, unmounted, or the connection
    // moved to another org.
    if (stopCode || isCancelled() || !isMounted()) {
      report(leadId, LEAD_NOT_PROCESSED);
      continue;                                          // eslint-disable-line no-continue
    }
    progress(index + 1);

    const entry = entries.get(leadId);
    let leadResult;
    let leadCode = null;

    if (dirtyLeadIds && dirtyLeadIds.has(leadId)) {
      leadResult = LEAD_UNSAVED;
    } else if (!entry || !entry.approved || entry.creation_state === 'not_mappable') {
      // Not this company's, not approved, or nothing to map: the backend would refuse it.
      leadResult = LEAD_NOT_ELIGIBLE;
      leadCode = entry && entry.creation_state === 'not_mappable' ? 'lead_not_mappable' : null;
    } else if (entry.creation_state === 'created') {
      leadResult = LEAD_ALREADY_CREATED;
    } else if (!CREATABLE.includes(entry.creation_state)) {
      leadResult = { duplicate: LEAD_DUPLICATE, creation_unknown: LEAD_UNKNOWN,
        creating: LEAD_BUSY }[entry.creation_state] || LEAD_FAILED;
      leadCode = entry.creation_error_code || null;
    } else {
      // THE CREATE. The org this run started against travels with it, so the backend refuses
      // rather than creating this lead somewhere the user did not choose.
      try {
        const response = await axios.post(                // eslint-disable-line no-await-in-loop
          `${API_URL}/salesforce/leads/${leadId}`,
          expectedOrgId ? { expected_org_id: expectedOrgId } : null,
          authConfig(token),
        );
        leadResult = CREATE_OUTCOME[response.data && response.data.state] || LEAD_FAILED;
        leadCode = (response.data && response.data.error_code) || null;
        // The same definitive code can arrive as a STORED outcome — P-SF3's own pin, after
        // a refresh inside the attempt, answers 200 with state failed — and it stops the
        // batch exactly as the refusal below does.
        if (leadCode === CONNECTION_CHANGED) stopCode = CONNECTION_CHANGED;
      } catch (error) {
        const code = error && error.response && error.response.data
          && error.response.data.detail;
        if (code === CONNECTION_CHANGED) {
          // The one failure that stops the batch: the rest of the selection would be created
          // in an org the user never chose.
          stopCode = CONNECTION_CHANGED;
          leadResult = LEAD_FAILED;
          leadCode = code;
        } else if (code === 'lead_not_mappable' || code === 'lead_not_approved') {
          leadResult = LEAD_NOT_ELIGIBLE;
          leadCode = code;
        } else if (!error || !error.response) {
          // No answer at all. The write may have happened; the read-back decides.
          leadResult = LEAD_UNKNOWN_RESPONSE;
        } else {
          leadResult = LEAD_FAILED;
          leadCode = typeof code === 'string' ? code : 'request_failed';
        }
      }
    }

    // THE CAMPAIGN, if one was chosen. A second, independent operation: it runs only for a
    // lead that IS in Salesforce, and whatever happens to it, the lead result above stands.
    let campaignResult = campaignId ? CAMPAIGN_SKIPPED : CAMPAIGN_NONE;
    let campaignCode = null;
    const inSalesforce = [LEAD_CREATED, LEAD_ALREADY_CREATED].includes(leadResult);
    if (campaignId && inSalesforce && !stopCode) {
      if (campaignIsSettled(entry)) {
        // Already recorded as a member of this Campaign: the work is done, so it is not
        // done again.
        campaignResult = MEMBER_OUTCOME[entry.membership_state];
      } else if (entry && entry.membership_state === 'adding') {
        campaignResult = CAMPAIGN_SKIPPED;               // somebody's attempt is in flight
      } else if (leadResult === LEAD_ALREADY_CREATED && entry
                 && entry.origin_matches_current_org === false) {
        // Created in a different org than the one connected: P-SF4 would refuse it, so the
        // request is not made.
        campaignResult = CAMPAIGN_SKIPPED;
        campaignCode = CONNECTION_CHANGED;
      } else {
        try {
          const response = await axios.post(              // eslint-disable-line no-await-in-loop
            `${API_URL}/salesforce/leads/${leadId}/campaign-memberships`,
            { campaign_id: campaignId },
            authConfig(token),
          );
          campaignResult = MEMBER_OUTCOME[response.data && response.data.state]
            || CAMPAIGN_FAILED;
          campaignCode = (response.data && response.data.error_code) || null;
          // P-SF4's in-attempt pin stores the same code as a failed outcome (200).
          if (campaignCode === CONNECTION_CHANGED) stopCode = CONNECTION_CHANGED;
        } catch (error) {
          const code = error && error.response && error.response.data
            && error.response.data.detail;
          campaignResult = CAMPAIGN_FAILED;
          campaignCode = typeof code === 'string' ? code : 'request_failed';
          if (code === CONNECTION_CHANGED) stopCode = CONNECTION_CHANGED;
        }
      }
    }

    report(leadId, leadResult, { campaign: campaignResult, leadCode, campaignCode });
    progress(index + 1);
  }

  return { results, stopCode };
};

/**
 * The summary, rebuilt from what the SERVER holds once the run is over.
 *
 * The local run is consulted only for the two things the server cannot know: which leads
 * were never started, and which were held back here (unsaved edits). Everything else — what
 * exists in Salesforce — comes from the read-back, so a lost answer resolves to what actually
 * happened instead of being reported as a failure.
 */
export const mergeFinalResults = ({ leadIds, finalPlan, localResults }) => {
  const merged = {};
  if (!finalPlan) {
    // The read-back itself did not answer. Nothing is downgraded to a failure because of
    // that: a lead whose create went unanswered stays "check Salesforce", which is exactly
    // what is known about it.
    leadIds.forEach((leadId) => {
      const local = localResults[leadId] || { lead: LEAD_NOT_PROCESSED, campaign: CAMPAIGN_NONE };
      merged[leadId] = {
        ...local,
        lead: local.lead === LEAD_UNKNOWN_RESPONSE ? LEAD_UNKNOWN : local.lead,
      };
    });
    return merged;
  }
  const entries = entriesById(finalPlan);
  const wantsCampaign = Boolean(finalPlan.campaign_id);

  leadIds.forEach((leadId) => {
    const local = localResults[leadId] || { lead: LEAD_NOT_PROCESSED, campaign: CAMPAIGN_NONE };
    const entry = entries.get(leadId);

    // Held back here, or never started: there is nothing on the server to read.
    if ([LEAD_UNSAVED, LEAD_NOT_PROCESSED].includes(local.lead)) {
      merged[leadId] = { ...local, campaign: wantsCampaign ? CAMPAIGN_SKIPPED : CAMPAIGN_NONE };
      return;
    }

    let lead;
    let leadCode = local.leadCode || null;
    if (!entry || !entry.approved) {
      lead = LEAD_NOT_ELIGIBLE;
    } else if (entry.creation_state === 'created') {
      // Created by THIS run only when this run actually sent the create — an answer that
      // said so, or one that was lost. A lead somebody else was creating (busy here) that
      // finished meanwhile is already in Salesforce, not this bulk's doing.
      lead = [LEAD_CREATED, LEAD_UNKNOWN_RESPONSE].includes(local.lead)
        ? LEAD_CREATED : LEAD_ALREADY_CREATED;
      leadCode = null;
    } else if (entry.creation_state === 'not_mappable') {
      lead = LEAD_NOT_ELIGIBLE;
      leadCode = leadCode || 'lead_not_mappable';
    } else if (entry.creation_state === 'ready') {
      // Still ready: nothing was written. Either it was never attempted, or the attempt was
      // refused before it claimed anything.
      lead = local.lead === LEAD_UNKNOWN_RESPONSE ? LEAD_FAILED : local.lead;
      if (![LEAD_FAILED, LEAD_NOT_ELIGIBLE, LEAD_NOT_PROCESSED].includes(lead)) {
        lead = LEAD_FAILED;
      }
    } else {
      lead = { duplicate: LEAD_DUPLICATE, creation_unknown: LEAD_UNKNOWN, creating: LEAD_BUSY,
        failed: LEAD_FAILED }[entry.creation_state] || LEAD_FAILED;
      leadCode = entry.creation_error_code || leadCode;
    }

    let campaign = wantsCampaign ? local.campaign : CAMPAIGN_NONE;
    let campaignCode = local.campaignCode || null;
    if (wantsCampaign && entry && entry.membership_state
        && entry.membership_state !== 'none') {
      // A membership record exists on the server: it is the truth about this pair.
      campaign = MEMBER_OUTCOME[entry.membership_state] || CAMPAIGN_FAILED;
      campaignCode = entry.membership_error_code || campaignCode;
    }
    if (campaign === CAMPAIGN_ADDED || campaign === CAMPAIGN_ALREADY) campaignCode = null;

    merged[leadId] = { lead, campaign, leadCode, campaignCode };
  });

  return merged;
};

/** How one lead's two outcomes read as a single line of the summary. */
const resultKey = (result) => {
  if ([CAMPAIGN_ADDED, CAMPAIGN_ALREADY].includes(result.campaign)) {
    return `${result.lead}+${result.campaign}`;
  }
  if (result.campaign === CAMPAIGN_FAILED) return `${result.lead}+campaign_failed`;
  return result.lead;
};

export const resultCounts = (merged, leadIds) => {
  const counts = {};
  leadIds.forEach((leadId) => {
    const result = merged[leadId];
    if (result) counts[resultKey(result)] = (counts[resultKey(result)] || 0) + 1;
  });
  return counts;
};

// Every line the summary can show, in the order it shows them: the successes first, then
// what is waiting on somebody, then what never went.
const SUMMARY_ORDER = [
  ['created+added', 'created + added to the campaign'],
  ['created+already_member', 'created + already in the campaign'],
  ['created', 'created'],
  ['created+campaign_failed', 'created, but not added to the campaign'],
  ['already_created+added', 'already created + added to the campaign'],
  ['already_created+already_member', 'already created + already in the campaign'],
  ['already_created', 'already in Salesforce'],
  ['already_created+campaign_failed', 'already created, but not added to the campaign'],
  ['duplicate', 'duplicate — review in Salesforce'],
  ['creation_unknown', 'unconfirmed — check in Salesforce'],
  ['busy', 'already being sent'],
  ['failed', 'failed'],
  ['failed+campaign_failed', 'failed'],
  ['not_eligible', 'not eligible'],
  ['unsaved_changes', 'held back — unsaved changes'],
  ['not_processed', 'not sent'],
];

export const summaryLines = (merged, leadIds) => {
  const counts = resultCounts(merged, leadIds);
  const known = SUMMARY_ORDER.filter(([key]) => counts[key])
    .map(([key, label]) => ({ key, count: counts[key], label }));
  // Anything the table above does not name still gets counted rather than disappearing.
  const named = new Set(SUMMARY_ORDER.map(([key]) => key));
  const rest = Object.keys(counts).filter((key) => !named.has(key))
    .map((key) => ({ key, count: counts[key], label: key.replace(/[+_]/g, ' ') }));
  return [...known, ...rest];
};

/** Live tally while the run is going: what is done, and what will want a look. */
export const runTally = (results) => {
  let completed = 0;
  let attention = 0;
  Object.values(results || {}).forEach((result) => {
    if (result.lead === LEAD_NOT_PROCESSED) return;
    const settled = [LEAD_CREATED, LEAD_ALREADY_CREATED].includes(result.lead)
      && result.campaign !== CAMPAIGN_FAILED;
    if (settled) completed += 1; else attention += 1;
  });
  return { completed, attention };
};

/** Whether a merged result is one the user needs to look at. */
export const needsAttention = (result) => !(
  [LEAD_CREATED, LEAD_ALREADY_CREATED].includes(result.lead)
  && result.campaign !== CAMPAIGN_FAILED);
